import { validationConstants } from "constants/formValidation";
import { Label } from "layout/typography/Label";
import { useMemo, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { TourAnchor } from "training/anchors";
import { IOrderTypesVisible } from "types/Account/Account";

import { StyledCard } from "../../CreateOrder.styled";
import { orderTypes } from "../Header/constants";
import { ORDER_TYPE_DESCRIPTIONS, withPatientEstimateFirst } from "./helper";
import { StyledOrderTypeSwitch } from "./styled";

export function OrderType({
  orderTypesVisible,
  isFetching,
  redo = false,
  embedded = false,
}: {
  orderTypesVisible?: IOrderTypesVisible;
  isFetching?: boolean;
  redo?: boolean;
  embedded?: boolean;
}) {
  const { control, setValue } = useFormContext();

  const orderTypesDropdown = useMemo(() => {
    if (isFetching) {
      return [];
    }
    let orderTypeValues = Object.values(orderTypes);
    if (orderTypesVisible) {
      orderTypeValues = orderTypeValues.filter((orderType) => {
        const key =
          orderType.value === "PatientResponsibility"
            ? "patientResponsibility"
            : orderType.value.toLowerCase();
        return orderTypesVisible[key] === true;
      });
    }
    return withPatientEstimateFirst(orderTypeValues);
  }, [orderTypes, orderTypesVisible]);

  useEffect(() => {
    if (orderTypesDropdown.length === 1) {
      setValue("orderType", orderTypesDropdown[0].value);
    }
  }, [orderTypesDropdown]);

  const content = (
    <div
      className="flex flex-column gap-4 w-100"
      data-tour={TourAnchor.OrderType}
    >
      <div className="formgrid grid w-100">
        <div className="field flex flex-column col-12">
          <Label htmlFor="orderType" data-testid="orderTypeLabel">
            Order Type
            {isFetching && <i className="pi pi-spin pi-spinner ml-2 text-xs" />}
          </Label>

          <Controller
            data-testid="orderType"
            name="orderType"
            control={control}
            rules={{
              required: validationConstants.VISIT_INFO_FORM.DATE_OF_SERVICE,
            }}
            render={({ field: { onChange, value } }) => (
              <>
                <StyledOrderTypeSwitch
                  id="orderType"
                  data-testid="orderType"
                  value={value}
                  onChange={onChange}
                  options={orderTypesDropdown}
                  optionLabel="name"
                  unselectable={false}
                  disabled={isFetching || redo}
                />

                {!!ORDER_TYPE_DESCRIPTIONS[value] && (
                  <span
                    data-testid="orderTypeDescription"
                    className="text-xs mt-2"
                    style={{ color: "var(--brand-gray-500)" }}
                  >
                    {ORDER_TYPE_DESCRIPTIONS[value]}
                  </span>
                )}
              </>
            )}
          />
        </div>
      </div>
    </div>
  );

  if (embedded) {
    return <div data-testid="orderTypeTitle">{content}</div>;
  }

  return (
    <StyledCard data-testid="orderTypeTitle" title="Type">
      {content}
    </StyledCard>
  );
}
